import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { BasketItem } from '../types';
import { BASKET_STORAGE_KEY } from '../constants';
import { resolveEffectivePeriod, findPriceForPeriod } from '@shared/pricing';

interface BasketStore {
  items: BasketItem[];
  orderName: string;
  note: string;
  addItem: (item: BasketItem) => void;
  removeItem: (productId: string) => void;
  updateLicensePeriod: (productId: string, months: number) => void;
  /** Apply a requested period to all AVCS items (batch change). */
  batchChangeAvcs: (requestedPeriodMonths: number) => void;
  setOrderName: (name: string) => void;
  setNote: (note: string) => void;
  clearBasket: () => void;
  isInBasket: (productId: string) => boolean;
}

export const useBasket = create<BasketStore>()(
  persist(
    (set, get) => ({
      items: [],
      orderName: '',
      note: '',

      addItem: (item) => {
        if (get().items.some((i) => i.productId === item.productId)) return;
        set((state) => ({ items: [...state.items, item] }));
      },

      removeItem: (productId) => {
        set((state) => ({ items: state.items.filter((i) => i.productId !== productId) }));
      },

      updateLicensePeriod: (productId, months) => {
        set((state) => ({
          items: state.items.map((i) => {
            if (i.productId !== productId) return i;
            const price = i.availablePrices
              ? findPriceForPeriod(i.availablePrices, months)
              : undefined;
            return {
              ...i,
              licensePeriodMonths: months,
              priceAmount: price?.priceAmount ?? i.priceAmount,
              currencyCode: price?.currencyCode ?? i.currencyCode,
            };
          }),
        }));
      },

      batchChangeAvcs: (requestedPeriodMonths) => {
        set((state) => ({
          items: state.items.map((item) => {
            if (item.productType !== 'AVCS' || !item.availablePrices?.length) return item;
            const effective = resolveEffectivePeriod(item.availablePrices, requestedPeriodMonths);
            if (effective === undefined) return item;
            const price = findPriceForPeriod(item.availablePrices, effective);
            return {
              ...item,
              licensePeriodMonths: effective,
              priceAmount: price?.priceAmount ?? item.priceAmount,
              currencyCode: price?.currencyCode ?? item.currencyCode,
            };
          }),
        }));
      },

      setOrderName: (name) => set({ orderName: name }),

      setNote: (note) => set({ note }),

      clearBasket: () => set({ items: [], orderName: '', note: '' }),

      isInBasket: (productId) => get().items.some((i) => i.productId === productId),
    }),
    { name: BASKET_STORAGE_KEY },
  ),
);
